import ApiClient from './ApiClient';

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

// The stripe/status and stripe/connect DTOs are NOT wire-pinned to camelCase (unlike
// GET features — see FeaturesResponseDto) — they serialize as the exact C# property names
// (PascalCase: Connected, ChargesEnabled, AuthorizeUrl). stripe/disconnect's response is already
// camelCase (the handler writes it as an anonymous-object literal). Normalize at the client
// boundary, mirroring ColoClient's toCamelCaseShallow convention, so the rest of the app never has
// to know about the split.
const toCamelKey = key => key.charAt(0).toLowerCase() + key.slice(1);

const toCamelCaseShallow = obj => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [toCamelKey(key), value])
  );
};

class StripeClient extends ApiClient {
  constructor(url) {
    super(url ? `${url}/stripe` : '/stripe');
  }

  // GET stripe/status — admin-only read of the caller's own org's Stripe connection status.
  // Returns { connected, chargesEnabled }.
  async getStatus() {
    try {
      const data = await this.get('/status');
      return toCamelCaseShallow(data);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch Stripe status.')
      );
    }
  }

  // POST stripe/connect — admin-only; returns { authorizeUrl } to redirect the browser to.
  async connect() {
    try {
      const data = await this.post('/connect');
      return toCamelCaseShallow(data);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to initiate Stripe Connect.')
      );
    }
  }

  // POST stripe/disconnect — admin-only; clears the org's Stripe Connect fields.
  async disconnect() {
    try {
      return await this.post('/disconnect');
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to disconnect Stripe.')
      );
    }
  }
}

export default StripeClient;
